import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfileHydration } from '@/hooks';
import { cvService } from '@/services/cv.service';
import { Button, useToast } from '@/components/common';
import { ATSReport } from '@shared/ui/ats-report';
import { InlineLoader } from '@shared/ui/loading';
import '../../../shared/ui/ats-report/ats-report.css';
import type { FullATSScoreV3 } from '@shared/types/ats-report';
import { ShareScore } from '@/components/common/ShareScore';
import { JobMatchSection } from '@/components/dashboard/JobMatchSection';
import { KeywordInsightsCard } from '@/components/dashboard/KeywordInsightsCard';
import { profileService } from '@/services/profile.service';

/** Minimum profile fields needed for a meaningful ATS analysis */
function getProfileGaps(profile: any): string[] {
  const gaps: string[] = [];
  if (!profile.firstName && !profile.lastName) gaps.push('name');
  if (!profile.experience?.length) gaps.push('work experience');
  if (!profile.skills?.length) gaps.push('skills');
  if (!profile.education?.length) gaps.push('education');
  return gaps;
}

export function DashboardPage() {
  const { profile } = useProfileHydration();
  const navigate = useNavigate();
  const toast = useToast();
  const [atsScoreV3, setAtsScoreV3] = useState<FullATSScoreV3 | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastAnalyzed, setLastAnalyzed] = useState<Date | null>(null);

  // Load saved V3 analysis from localStorage
  useEffect(() => {
    const savedV3 = localStorage.getItem('ats-analysis-v3');
    if (savedV3) {
      try {
        const data = JSON.parse(savedV3);
        setAtsScoreV3(data.analysis);
        setLastAnalyzed(new Date(data.timestamp));
      } catch (error) {
        console.error('Failed to load ATS V3 analysis:', error);
      }
    }
  }, []);

  const handleAnalyzeATS = async () => {
    if (!profile) return;

    // Check profile completeness
    const gaps = getProfileGaps(profile);
    if (gaps.length > 2) {
      toast.error(
        `Your profile needs more data for a meaningful analysis. Please add: ${gaps.join(', ')}.`
      );
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await cvService.analyzeATSV3();
      setAtsScoreV3(result);
      setLastAnalyzed(new Date());

      const now = new Date().toISOString();
      localStorage.setItem(
        'ats-analysis-v3',
        JSON.stringify({
          analysis: result,
          timestamp: now,
        })
      );

      // Persist ATS score to profile (syncs to extension/server)
      const overallScore = result.report?.overallScore;
      if (overallScore != null) {
        profileService.patchProfile({
          lastATSScore: overallScore,
          lastATSAnalyzedAt: now,
        } as any).catch((err) => console.warn('Failed to persist ATS score to profile:', err));
      }

      toast.success('ATS analysis complete!');
    } catch (error: any) {
      console.error('ATS V3 analysis failed:', error);
      // Skip toast if QuotaWall already handles 429
      if (error?.response?.status === 429) return;
      const message =
        error instanceof Error ? error.message : 'Analysis failed. Please try again.';
      toast.error(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // No profile yet: show CTA to create profile
  if (!profile) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">ATS Analysis</h1>
        <p className="text-text-secondary">
          Check how well your resume passes Applicant Tracking Systems used by employers.
        </p>
        <div className="bg-surface border border-border rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-text-primary mb-2">Welcome to ATS CV Checker</h2>
          <p className="text-text-secondary mb-4">
            Create your profile to enable ATS analysis, recommendations, and resume generation.
          </p>
          <Button onClick={() => navigate('/app/onboarding')} size="md">
            Create Profile
          </Button>
        </div>
      </div>
    );
  }

  const gaps = getProfileGaps(profile);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">ATS Analysis</h1>
          <p className="mt-1 text-text-secondary">
            Check how well your profile data performs against Applicant Tracking Systems used by employers.
          </p>
        </div>
        {lastAnalyzed && (
          <span className="text-sm text-text-secondary">
            Last analyzed:{' '}
            {lastAnalyzed.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>

      {/* Profile gaps warning */}
      {gaps.length > 0 && !atsScoreV3 && (
        <div className="bg-warning-soft border border-warning rounded-lg p-4">
          <p className="text-sm text-text-primary">
            <strong>Tip:</strong> For a more accurate analysis, add{' '}
            <strong>{gaps.join(', ')}</strong> to your{' '}
            <button
              onClick={() => navigate('/app/profile')}
              className="text-primary underline hover:no-underline"
            >
              profile
            </button>
            .
          </p>
        </div>
      )}

      {/* ATS Analysis Section */}
      {atsScoreV3 ? (
        <div className="space-y-4">
          <ATSReport data={atsScoreV3} mode="full" backendUrl={import.meta.env.VITE_BACKEND_URL || 'https://api.atscvchecker.pro'} />
          {atsScoreV3.report?.overallScore && (
            <ShareScore score={atsScoreV3.report.overallScore} />
          )}
          <div className="flex justify-center">
            <Button
              onClick={handleAnalyzeATS}
              disabled={isAnalyzing}
              variant="outline"
              size="sm"
            >
              {isAnalyzing ? (
                <span className="flex items-center gap-2">
                  <InlineLoader size="sm" /> Re-analyzing...
                </span>
              ) : (
                'Re-analyze with current profile data'
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-lg p-6 sm:p-8 text-center shadow-sm">
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            ATS Compatibility Check
          </h2>
          <p className="text-text-secondary mb-2">
            We'll analyze the experience, skills, and education from your{' '}
            <button
              onClick={() => navigate('/app/profile')}
              className="text-primary underline hover:no-underline"
            >
              profile
            </button>{' '}
            and score them for ATS compatibility.
          </p>
          <p className="text-xs text-text-secondary mb-4">
            We'll check formatting, keyword usage, section structure, and more. Takes about 15-30
            seconds.
          </p>

          {isAnalyzing ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <InlineLoader size="lg" />
              <p className="text-sm text-text-secondary">
                Analyzing your profile... This usually takes 15-30 seconds.
              </p>
            </div>
          ) : (
            <Button onClick={handleAnalyzeATS} size="md">
              Analyze My Profile
            </Button>
          )}
        </div>
      )}

      {/* Trending Keywords */}
      <KeywordInsightsCard />

      {/* Job Match Section */}
      <JobMatchSection />
    </div>
  );
}
